import { AUDIO_BUS_KEYS, type AudioFrame, SILENT_BUSES } from "@dodobop/audio";
import type { ChartProvenance } from "./provenance.ts";
import { type ChartSeed, type Difficulty, MAX_HOLD_MS } from "./rhythm.ts";
import type { VisualCue } from "./visualPlan.ts";

/**
 * A game somebody else can play.
 *
 * The player's browser cannot analyse the audio: a track hosted anywhere the creator chooses
 * arrives without CORS headers, and Web Audio hands back silence for a file it is not allowed
 * to read. Measured, and it also kills the sound, because the graph is the route to the
 * speakers. So the analysis travels with the game instead of being redone, and the player's
 * device only has to play a file and keep time against it.
 *
 * That turns out to be the better arrangement anyway. Everyone who plays a track sees exactly
 * the same show, because they are all reading the same analysis rather than each running their
 * own against whatever their device did to the audio.
 *
 * The audio itself is never here, only a URL to wherever the creator put it. Nothing in this
 * file is a copy of anybody's music.
 */

export const SHARE_VERSION = 4;

/**
 * Frames a second in a shared timeline.
 *
 * Analysis runs at about sixty, and storing that outright is 720 bytes a second: two hundred
 * kilobytes for a four minute track, mostly the same number repeated.
 *
 * Measured rather than chosen. Resampling error grows smoothly as the rate falls, so there is
 * no cliff to find in it, but peaks are a different matter: at 30 and above the timeline keeps
 * 99.8% of every bus's peak, and at 20 that falls to 92%, which is transients starting to go
 * missing. Thirty is the lowest rate that still carries them, at 360 bytes a second.
 */
export const SHARE_HZ = 30;

/** Buses are 0..1, so a byte apiece is about four decimal places more than the eye can see. */
const LEVELS = 255;

/**
 * What is carried past the buses, and why it cannot be left out.
 *
 * The buses are envelopes, and an envelope is not an event. `onset` is still high a couple of
 * hundred milliseconds after the hit that caused it, so no threshold on the receiving side
 * turns it back into the moment; a beat has to travel as an edge or it does not travel. The
 * same goes for a drop, which is the one thing the engine treats as violent, and for the build
 * behind it, which is a level nothing else implies. Measured on a real track: the first version
 * of this format carried 662 beats, 2 drops and a 14-second rise as zero, zero and nothing, and
 * the show it produced drifted along with the loudness and never marked anything.
 *
 * Three bytes a frame: the build, the tempo, and a flags byte for the two edges. 120 bytes a
 * second on top of the buses' 270.
 */
const EXTRA_BYTES = 3;
const STRIDE = AUDIO_BUS_KEYS.length + EXTRA_BYTES;
const BUILD_AT = AUDIO_BUS_KEYS.length;
const BPM_AT = AUDIO_BUS_KEYS.length + 1;
const FLAGS_AT = AUDIO_BUS_KEYS.length + 2;
const BEAT_BIT = 1;
const DROP_BIT = 2;

export interface SharedGame {
  readonly v: number;
  readonly title: string;
  readonly artist: string;
  /** Whoever made the game, as opposed to whoever made the song. A name, not a claim. */
  readonly maker?: string;
  /**
   * The track's own picture, where it came with one.
   *
   * A full address rather than an id, because it is served from whoever hosts the track. Absent
   * is normal and costs a blank tile, never a game.
   */
  readonly art?: string;
  /**
   * What the track was asked to sound like, in its creator's words.
   *
   * Written before the song existed, which makes it a better description of its character than
   * anything that can be worked out from the audio afterwards.
   */
  readonly style?: string;
  /**
   * The words, when the track came with any.
   *
   * Text only, and deliberately not on the shelf card: a lyric is a page of writing and a card
   * is a line, so this is only ever read by whoever opened the game itself.
   */
  readonly lyrics?: string;
  /** Wherever the creator put the track. Never a copy of it. */
  readonly audio: string;
  readonly durationSeconds: number;
  readonly bpm: number;
  readonly sceneId: string;
  readonly lookId: string;
  readonly chart: readonly ChartSeed[];
  /** Which analysis and chart writer produced those notes, and a name for the set of them. */
  readonly provenance?: ChartProvenance;
  /** When the show changes, decided from the shape of the record rather than by a timer. */
  readonly cues?: readonly VisualCue[];
  /** Buses at SHARE_HZ, one byte each, in AUDIO_BUS_KEYS order. */
  readonly buses: string;
  readonly frames: number;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Base64 without a dependency, and without a megabyte-long argument list to spread. */
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Resamples the analysis onto a fixed grid.
 *
 * Nearest frame rather than an average: these are envelopes that have already been smoothed by
 * the analysis, and averaging them again would round off the attacks that make a scene move.
 *
 * The edges are the exception, and they are gathered rather than sampled. Halving the rate
 * means every other analysis frame is passed over, and a beat that happened in one of those is
 * a beat that never happened. So every frame is looked at for edges even though only one of
 * them is read for levels.
 */
export function packBuses(
  frames: readonly AudioFrame[],
  durationSeconds: number,
  hz = SHARE_HZ,
): string {
  const count = Math.max(1, Math.round(durationSeconds * hz));
  const bytes = new Uint8Array(count * STRIDE);
  if (frames.length === 0) return toBase64(bytes);

  let cursor = 0;
  let scanned = 0;
  let lastBeat = frames[0]!.beatCount;

  for (let i = 0; i < count; i++) {
    const at = i / hz;
    while (cursor < frames.length - 1 && frames[cursor + 1]!.tSeconds <= at) cursor++;
    const frame = frames[cursor]!;
    const base = i * STRIDE;

    for (let b = 0; b < AUDIO_BUS_KEYS.length; b++) {
      bytes[base + b] = Math.round(clamp01(frame.buses[AUDIO_BUS_KEYS[b]!]) * LEVELS);
    }
    bytes[base + BUILD_AT] = Math.round(clamp01(frame.build ?? 0) * LEVELS);
    bytes[base + BPM_AT] = Math.min(255, Math.max(0, Math.round(frame.bpm)));

    let flags = 0;
    for (; scanned <= cursor; scanned++) {
      const seen = frames[scanned]!;
      if (seen.beatCount !== lastBeat) flags |= BEAT_BIT;
      lastBeat = seen.beatCount;
      if (seen.drop === true) flags |= DROP_BIT;
    }
    bytes[base + FLAGS_AT] = flags;
  }
  return toBase64(bytes);
}

/**
 * Reads a packed timeline back as frames the stage can be driven with.
 *
 * Read by width rather than by version, so a link shared before the edges travelled still
 * plays. It gets the show it was made with, which is the one its creator saw.
 */
export function unpackBuses(packed: string, frames: number, hz = SHARE_HZ): AudioFrame[] {
  const bytes = fromBase64(packed);
  const stride = bytes.length >= frames * STRIDE ? STRIDE : AUDIO_BUS_KEYS.length;
  const withEdges = stride === STRIDE;
  const out: AudioFrame[] = [];
  let beatCount = 0;

  for (let i = 0; i < frames; i++) {
    const base = i * stride;
    if (base + stride > bytes.length) break;
    const buses: Record<string, number> = { ...SILENT_BUSES };
    for (let b = 0; b < AUDIO_BUS_KEYS.length; b++) {
      buses[AUDIO_BUS_KEYS[b]!] = bytes[base + b]! / LEVELS;
    }

    const flags = withEdges ? bytes[base + FLAGS_AT]! : 0;
    if ((flags & BEAT_BIT) !== 0) beatCount++;

    out.push({
      tSeconds: i / hz,
      buses: buses as unknown as AudioFrame["buses"],
      bpm: withEdges ? bytes[base + BPM_AT]! : 0,
      beatCount,
      build: withEdges ? bytes[base + BUILD_AT]! / LEVELS : 0,
      drop: (flags & DROP_BIT) !== 0,
    });
  }
  return out;
}

/** The frame for a moment, for a stage being driven by a shared timeline rather than by ears. */
export function frameAtSeconds(frames: readonly AudioFrame[], seconds: number): AudioFrame | null {
  if (frames.length === 0) return null;
  const index = Math.round(seconds * SHARE_HZ);
  return frames[Math.min(frames.length - 1, Math.max(0, index))] ?? null;
}

/**
 * Everything a shared game must have to be playable, checked rather than assumed.
 *
 * This arrives from a link a stranger sent, so nothing in it is trusted. The audio URL in
 * particular becomes the source of a media element, so anything that is not plainly an http
 * address is refused rather than handed to the browser.
 */
export function readSharedGame(value: unknown): SharedGame | null {
  if (typeof value !== "object" || value === null) return null;
  const raw = value as Record<string, unknown>;

  if (raw.v !== SHARE_VERSION && raw.v !== 1 && raw.v !== 2 && raw.v !== 3) return null;
  const audio = typeof raw.audio === "string" ? raw.audio.trim() : "";
  if (!isPlayableUrl(audio)) return null;

  const duration = typeof raw.durationSeconds === "number" ? raw.durationSeconds : 0;
  if (!Number.isFinite(duration) || duration <= 0 || duration > 3600) return null;

  const chart = Array.isArray(raw.chart) ? raw.chart : [];
  const seeds: ChartSeed[] = [];
  for (const entry of chart) {
    if (typeof entry !== "object" || entry === null) continue;
    const seed = entry as Record<string, unknown>;
    const time = typeof seed.time === "number" ? seed.time : Number.NaN;
    if (!Number.isFinite(time) || time < 0) continue;
    // Absent on anything charted before difficulty existed, and those games play as they were
    // made rather than pretending to a choice they cannot offer.
    const over = typeof seed.over === "number" && Number.isFinite(seed.over) ? seed.over : null;
    const pitch =
      typeof seed.pitch === "number" && Number.isFinite(seed.pitch)
        ? Math.min(1, Math.max(0, seed.pitch))
        : null;
    // Absent on every chart written before holds existed, which is the same as a tap.
    const hold =
      typeof seed.hold === "number" && Number.isFinite(seed.hold) && seed.hold > 0
        ? Math.min(MAX_HOLD_MS, Math.round(seed.hold))
        : null;
    const tiers = Array.isArray(seed.tiers)
      ? seed.tiers
          .slice(0, 8)
          .map((value) => (typeof value === "number" && Number.isFinite(value) ? value : 0))
          .filter((value) => value > 0)
      : [];
    seeds.push({
      time,
      strong: seed.strong === true,
      ...(over === null ? {} : { over: Math.max(0, over) }),
      ...(pitch === null ? {} : { pitch }),
      ...(hold === null ? {} : { hold }),
      ...(tiers.length === 0 ? {} : { tiers }),
    });
  }
  if (seeds.length === 0) return null;

  const text = (field: unknown, limit: number): string =>
    typeof field === "string" ? field.slice(0, limit) : "";

  const provenance = readProvenance(raw.provenance);
  const cues = readCues(raw.cues);

  return {
    v: SHARE_VERSION,
    title: text(raw.title, 80),
    artist: text(raw.artist, 80),
    audio,
    durationSeconds: duration,
    bpm: typeof raw.bpm === "number" && raw.bpm > 0 && raw.bpm < 400 ? raw.bpm : 0,
    sceneId: text(raw.sceneId, 60),
    lookId: text(raw.lookId, 60),
    chart: seeds,
    ...(provenance === null ? {} : { provenance }),
    ...(cues.length === 0 ? {} : { cues }),
    buses: typeof raw.buses === "string" ? raw.buses : "",
    frames: typeof raw.frames === "number" && raw.frames > 0 ? Math.floor(raw.frames) : 0,
  };
}

/**
 * The show's own plan, when the game has one.
 *
 * Arrives from a link a stranger sent, so the scene index is clamped rather than trusted: it is
 * used to pick out of a catalogue, and an out-of-range one from a game made by a newer version
 * would otherwise be a hole where a scene should be.
 */
function readCues(value: unknown): VisualCue[] {
  if (!Array.isArray(value)) return [];
  const cues: VisualCue[] = [];
  for (const entry of value.slice(0, 200)) {
    if (typeof entry !== "object" || entry === null) continue;
    const cue = entry as Record<string, unknown>;
    const atMs = typeof cue.atMs === "number" ? cue.atMs : Number.NaN;
    const scene = typeof cue.scene === "number" ? Math.floor(cue.scene) : Number.NaN;
    if (!Number.isFinite(atMs) || atMs < 0 || !Number.isFinite(scene) || scene < 0) continue;
    cues.push({
      atMs,
      scene,
      intensity:
        typeof cue.intensity === "number" && Number.isFinite(cue.intensity)
          ? Math.min(1, Math.max(0, cue.intensity))
          : 0.5,
      cut: cue.cut === true,
    });
  }
  return cues.sort((a, b) => a.atMs - b.atMs);
}

/**
 * Where a chart came from, when it says.
 *
 * Anything shared before provenance existed simply has none, and that is worth being able to
 * tell: a game with no provenance is one whose notes cannot be compared with anybody else's with
 * any confidence, and saying so is more use than inventing a hash for it here.
 */
function readProvenance(value: unknown): ChartProvenance | null {
  if (typeof value !== "object" || value === null) return null;
  const raw = value as Record<string, unknown>;
  const hash = typeof raw.hash === "string" ? raw.hash.slice(0, 32) : "";
  if (!/^[0-9a-f]{8,32}$/.test(hash)) return null;

  const level = raw.difficulty;
  const difficulty: Difficulty =
    level === "easy" || level === "normal" || level === "hard" || level === "expert"
      ? level
      : "hard";
  const count = (field: unknown): number =>
    typeof field === "number" && Number.isFinite(field) ? Math.max(0, Math.floor(field)) : 0;

  return {
    analysisVersion: count(raw.analysisVersion),
    chartVersion: count(raw.chartVersion),
    difficulty,
    hash,
  };
}

/**
 * Only ever an ordinary web address.
 *
 * `javascript:` and `data:` are the ones that matter: both are accepted by some element
 * attributes and neither has any business being a track. Refusing everything except http
 * means a new scheme cannot become a hole later.
 */
export function isPlayableUrl(value: string): boolean {
  if (value.length === 0 || value.length > 2048) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Turns a share link into the file it points at, where that transform is known.
 *
 * Somewhere like Dropbox is not a page about music, it is the file behind a link that answers
 * with a page unless asked otherwise, so refusing it would be refusing a track somebody has
 * every right to share. Measured on a real link: `dl=0` answers 302 to text/html, `dl=1`
 * answers application/binary, and only `raw=1` answers audio/mpeg, which is the one a media
 * element will treat as a track.
 *
 * Only hosts whose behaviour has actually been checked. Guessing at a rewrite for somewhere
 * unknown would turn a link that merely needs a moment into one that is wrong.
 */
export function repairAudioUrl(value: string): string {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (host === "dropbox.com" || host.endsWith(".dropbox.com")) {
      url.searchParams.delete("dl");
      url.searchParams.set("raw", "1");
      return url.toString();
    }
    return value;
  } catch {
    return value;
  }
}

/**
 * Somewhere that serves pages about music rather than music.
 *
 * The likeliest mistake by a distance, because a share link is the thing a creator has to
 * hand and it looks exactly like a link to their song. It is not: a browser cannot play a web
 * page, and the failure is silent because a media element pointed at HTML fires no error at
 * all. It simply never loads.
 *
 * A list of known hosts rather than a guess at what an audio URL looks like, because plenty of
 * real audio is served from paths with no file extension.
 */
export function isPageNotAudio(value: string): boolean {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return [
      "suno.com",
      "soundcloud.com",
      "open.spotify.com",
      "spotify.com",
      "youtube.com",
      "youtu.be",
      "music.apple.com",
      "bandcamp.com",
      "tidal.com",
      "deezer.com",
    ].some((known) => host === known || host.endsWith(`.${known}`));
  } catch {
    return false;
  }
}
